import { useEffect, useState } from "react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";

import { acceptInviteFn, peekInviteFn, type PeekResult } from "@/braining/partners.functions";
import { waitText } from "@/server/login-throttle";
import { KIND_LABEL, MIN_PASSWORD } from "@/braining/partners";
import { BrainingLogo } from "@/braining/components/braining-logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Приглашение в кабинет партнёра.
 *
 * Ссылку сотрудник агентства пересылает сам: почту система не отправляет.
 * По ссылке подрядчик задаёт пароль — и сразу оказывается в кабинете.
 * Та же ссылка, выданная повторно, — сброс забытого пароля.
 *
 * Ссылка одноразовая: после входа гаснет. Чужому глазу страница по
 * протухшей ссылке не говорит ничего, кроме «недействительна».
 *
 * Токен — после `#` (`/invite#…`): эту часть адреса браузер на сервер не
 * шлёт, и она не оседает в журналах nginx и приложения, а токен — это
 * пароль на семь дней (аудит 15.10, находка 16). Поэтому страница читает
 * его уже в браузере и спрашивает сервер POST-запросом.
 */
export const Route = createFileRoute("/invite")({
  head: () => ({
    meta: [
      { title: "Приглашение — кабинет партнёра Braining" },
      // Адрес с токеном не должен уезжать дальше по переходам.
      { name: "referrer", content: "no-referrer" },
    ],
  }),
  component: InvitePage,
});

function InvitePage() {
  const [token, setToken] = useState("");
  const [peek, setPeek] = useState<PeekResult | null>(null);
  useEffect(() => {
    const t = window.location.hash.replace(/^#/, "");
    setToken(t);
    peekInviteFn({ data: { token: t } })
      .then(setPeek)
      .catch((e: unknown) => {
        console.error("[invite] ссылку не проверили", e);
        setPeek({ ok: false, reason: "invalid" });
      });
  }, []);
  const invite = peek?.ok ? peek.invite : null;
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const router = useRouter();

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    if (password !== repeat) {
      setError("Пароли не совпадают");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await acceptInviteFn({ data: { token, password } });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      await router.invalidate();
      await navigate({ to: "/cabinet" });
    } catch (err) {
      console.error("[invite] не удалось принять приглашение", err);
      setError("Не получилось. Попробуйте ещё раз.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <BrainingLogo className="h-11 w-auto text-foreground" />
        </div>

        {!peek ? (
          <p className="text-center text-sm text-muted-foreground">Проверяем ссылку…</p>
        ) : !invite ? (
          <div className="space-y-2 rounded-xl border bg-card p-6 text-sm shadow-sm">
            {!peek.ok && peek.reason === "throttled" ? (
              <>
                <h1 className="text-lg font-semibold">Слишком много попыток</h1>
                <p className="text-muted-foreground">
                  Попробуйте через {waitText(peek.wait ?? 60)}.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-lg font-semibold">Ссылка недействительна</h1>
                <p className="text-muted-foreground">
                  Она устарела или уже использована. Попросите новую у того, кто вас приглашал.
                </p>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4 rounded-xl border bg-card p-6 shadow-sm">
            <div>
              <h1 className="text-lg font-semibold">
                {invite.hasAccount ? "Новый пароль" : "Кабинет партнёра"}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {invite.name} · {KIND_LABEL[invite.kind]}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Почта для входа</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={invite.email}
                readOnly
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">
                {invite.hasAccount ? "Новый пароль" : "Придумайте пароль"}
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                minLength={MIN_PASSWORD}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
              <p className="text-xs text-muted-foreground">Не короче {MIN_PASSWORD} знаков.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="repeat">Ещё раз</Label>
              <Input
                id="repeat"
                type="password"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                disabled={busy}
              />
            </div>

            {error && (
              <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive-ink">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? "Сохраняем…" : invite.hasAccount ? "Сохранить и войти" : "Войти в кабинет"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
